import axios from "../../../../axios";
import { createSlice } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import * as types from "./types";
import keyBy from "lodash/keyBy";
import { createErrorReportingAsyncThunk } from "../../../helpers";
import {
  ResendInvoiceChange,
  resendInvoiceWithChangesReqT,
} from "dora-contracts";

const prefix = "data/economics/invoices";

export type Invoice = types.Invoice;

interface State {
  entities: { [key: string]: Invoice };
  ids: string[];
}

export const getFailedInvoices = createErrorReportingAsyncThunk(
  `${prefix}/getFailedInvoices`,
  async () => {
    const { data } = await axios.get("/api/e-conomic/invoices/failed");
    return tPromise.decode(types.invoicesT)(data);
  }
);

const resendInvoiceRequest = (id: string) =>
  axios.post(`/api/e-conomic/invoices/${id}/resend`);

// Plain resend, no other side effects — shared by CustomerPage and the
// invoicing overview's Faktureret tab, neither of which shows/needs the
// failed-invoices list below.
export const resendInvoice = createErrorReportingAsyncThunk(
  `${prefix}/resendInvoice`,
  async (id: string) => {
    await resendInvoiceRequest(id);
  }
);

// Resend AND refresh the failed-invoices list below — only for this duck's
// own failed-invoices page (InvoicesStatus.tsx). Use plain resendInvoice
// instead if a caller doesn't show/need that list.
// Reloads the failed-invoices list whether or not the resend went through:
// a rejected send (422 FINANCE_INVOICE_REJECTED) has just stored the finance
// system's new reasons on the invoice, and the list is where they are shown.
const thenRefreshFailedList = async (
  dispatch: (action: ReturnType<typeof getFailedInvoices>) => unknown,
  request: () => Promise<unknown>
) => {
  try {
    await request();
  } finally {
    await dispatch(getFailedInvoices());
  }
};

export const resendInvoiceAndRefreshFailedList = createErrorReportingAsyncThunk(
  `${prefix}/resendInvoiceAndRefreshFailedList`,
  async (id: string, thunkAPI) => {
    await thenRefreshFailedList(thunkAPI.dispatch, () =>
      resendInvoiceRequest(id)
    );
  }
);

export const resendInvoiceWithDueDate = createErrorReportingAsyncThunk(
  `${prefix}/resendInvoiceWithDueDate`,
  async (input: { id: string; dueDate: string }, thunkAPI) => {
    await thenRefreshFailedList(thunkAPI.dispatch, () =>
      axios.post(`/api/e-conomic/invoices/${input.id}/resend-with-due-date`, {
        dueDate: input.dueDate,
      })
    );
  }
);

export const resendInvoiceWithChanges = createErrorReportingAsyncThunk(
  `${prefix}/resendInvoiceWithChanges`,
  async (
    input: { id: string; changes: ResendInvoiceChange[] },
    thunkAPI
  ) => {
    await thenRefreshFailedList(thunkAPI.dispatch, () =>
      axios.post(
        `/api/e-conomic/invoices/${input.id}/resend-with-changes`,
        resendInvoiceWithChangesReqT.encode({ changes: input.changes })
      )
    );
  }
);

const initialState: State = {
  entities: {},
  ids: [],
};

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    failedInvoiceUpdated: (state, action: { payload: Invoice }) => {
      const invoice = action.payload;
      state.entities[invoice.id] = invoice;
      if (!state.ids.includes(invoice.id)) {
        state.ids.push(invoice.id);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFailedInvoices.fulfilled, (_state, action) => {
      return {
        entities: keyBy(action.payload, "id"),
        ids: action.payload.map((x) => x.id),
      };
    });
  },
});

export const { failedInvoiceUpdated } = slice.actions;

export default slice.reducer;
